import { zodResolver } from "@hookform/resolvers/zod";
import { useQuery } from "@tanstack/react-query";
import { useRouter } from "@tanstack/react-router";
import { useState } from "react";
import { useForm } from "react-hook-form";
import { toast } from "sonner";
import { z } from "zod";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader } from "@/components/ui/card";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { signIn } from "@/lib/auth-client";
import { DEV_ADMIN_PASSWORD, DEV_CP_PASSWORD, DEV_USER_PASSWORD } from "@/lib/dev-accounts";
import { devKeys } from "@/lib/query";

const loginSchema = z.object({
  email: z.string().email("Invalid email address"),
  password: z.string().min(8, "Password must be at least 8 characters"),
});

type LoginValues = z.infer<typeof loginSchema>;

const isDev = process.env.NODE_ENV !== "production";

const seedAccountsSchema = z.object({
  admin: z.object({ email: z.string().email() }),
  user: z.object({ email: z.string().email() }),
  cp: z.object({ email: z.string().email() }),
});

type DevAccount = { label: string; email: string; password: string };

// Emails come from a dev-only server endpoint so they always match the seeded
// rows, regardless of which host the studio opened the app at.
function useDevAccounts() {
  return useQuery({
    queryKey: devKeys.seedAccounts,
    enabled: isDev,
    staleTime: Number.POSITIVE_INFINITY,
    queryFn: async (): Promise<DevAccount[]> => {
      const res = await fetch("/api/dev/seed-accounts");
      if (!res.ok) return [];
      const parsed = seedAccountsSchema.parse(await res.json());
      return [
        { label: "Admin", email: parsed.admin.email, password: DEV_ADMIN_PASSWORD },
        { label: "User", email: parsed.user.email, password: DEV_USER_PASSWORD },
        { label: "Client Partner", email: parsed.cp.email, password: DEV_CP_PASSWORD },
      ];
    },
  });
}

export function LoginForm() {
  const router = useRouter();
  const [microsoftPending, setMicrosoftPending] = useState(false);
  const form = useForm<LoginValues>({
    resolver: zodResolver(loginSchema),
    defaultValues: { email: "", password: "" },
  });
  const devAccounts = useDevAccounts().data ?? [];

  function fillCredentials(email: string, password: string) {
    form.setValue("email", email, { shouldValidate: true });
    form.setValue("password", password, { shouldValidate: true });
  }

  async function onSubmit(values: LoginValues) {
    const { error } = await signIn.email({
      email: values.email,
      password: values.password,
    });

    if (error) {
      toast.error(error.message ?? "Failed to sign in");
      return;
    }

    router.navigate({ to: "/dashboard" });
  }

  return (
    <Card className="border-border/50 shadow-sm">
      <CardHeader className="space-y-1 text-center pb-2">
        <h1 className="font-display text-3xl font-bold">Nymbl Profitability</h1>
        <p className="text-muted-foreground text-sm">Sign in with your Nymbl account</p>
      </CardHeader>
      <CardContent>
        {/*
          Microsoft is the way in. The provider was configured server-side, but
          without this button there was no way to start the flow at all -- the
          page offered only the email and password form, which is disabled in
          production.
        */}
        <Button
          type="button"
          variant="outline"
          className="w-full"
          disabled={microsoftPending}
          onClick={async () => {
            setMicrosoftPending(true);
            try {
              await signIn.social({ provider: "microsoft", callbackURL: "/dashboard/accounts" });
            } catch (error) {
              setMicrosoftPending(false);
              toast.error(
                error instanceof Error ? error.message : "Could not start Microsoft sign-in",
              );
            }
          }}
        >
          {microsoftPending ? "Redirecting to Microsoft..." : "Sign in with Microsoft"}
        </Button>

        {isDev ? (
          <div className="relative my-4">
            <div className="absolute inset-0 flex items-center">
              <span className="w-full border-t" />
            </div>
            <div className="relative flex justify-center text-xs uppercase">
              <span className="bg-card text-muted-foreground px-2">or, in development only</span>
            </div>
          </div>
        ) : null}

        {/* Email and password is disabled server-side in production; showing the
            form there would offer a door that cannot open. */}
        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)} className={isDev ? "space-y-4" : "hidden"}>
            <FormField
              control={form.control}
              name="email"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Email</FormLabel>
                  <FormControl>
                    <Input type="email" placeholder="you@example.com" {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="password"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Password</FormLabel>
                  <FormControl>
                    <Input type="password" placeholder="••••••••" {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <Button type="submit" className="w-full" disabled={form.formState.isSubmitting}>
              {form.formState.isSubmitting ? "Signing in…" : "Sign In"}
            </Button>
          </form>
        </Form>
        {isDev && devAccounts.length > 0 && (
          <div className="mt-4 flex items-center justify-center gap-2">
            <span className="text-xs text-muted-foreground">Sign In As:</span>
            {devAccounts.map((account) => (
              <Button
                key={account.label}
                type="button"
                variant="outline"
                size="sm"
                onClick={() => fillCredentials(account.email, account.password)}
              >
                {account.label}
              </Button>
            ))}
          </div>
        )}
        {/* No public signup: access is by invitation, and an account that is
            without an access grant cannot create a session even with a valid
            Microsoft login. Offering a Sign up link would send people to a door
            that does not open. */}
        <p className="text-center text-sm text-muted-foreground mt-6">
          Access is by invitation. Ask Zack to be added.
        </p>
      </CardContent>
    </Card>
  );
}
